/**
 * ObservationActions — renders the structured `actions` array on a Coach
 * observation as one-click buttons.
 *
 * Used by every UI surface that renders observation cards (JournalView's
 * inline session-group cards, CoachObservationsPanel for Setups/ATM, the
 * future Insights modal). Keeps the rendering + UX pattern in ONE place.
 *
 * Behavior:
 *   - Renders one button per action in `obs.actions`
 *   - If `action.confirmText` is set, click pops a small confirm prompt
 *     (browser `confirm()` for now — replaced with a proper modal once we
 *     add one for other flows)
 *   - On confirm, calls onAction(action, observation)
 *   - On success, the button is HIDDEN PERMANENTLY (this component's
 *     lifetime). The optimistic-hide is keyed by a fingerprint of the
 *     action's type + payload so the right button stays hidden even if
 *     the parent observation re-renders with the same action still in
 *     its array (e.g., if the analyzer cache hasn't refreshed yet).
 *     Without this, the button briefly flashes "Done ✓" then reverts —
 *     looks like the click didn't take.
 *
 * Props:
 *   observation    The full Observation object
 *   onAction       (action, observation) => Promise|void — parent dispatcher
 *   darkMode       bool
 *   compact        bool — tighter padding for inline use in narrow cards
 */

import { useState } from 'react';
import { Check } from 'lucide-react';

// Stable fingerprint for an action so we can track "this exact action was
// already executed" across parent re-renders. Index alone isn't safe —
// the analyzer can re-emit actions in a different order. Type + serialized
// payload + label is unique enough for the cases we have today.
const actionFingerprint = (action) => {
  try {
    return `${action.type}::${action.label || ''}::${JSON.stringify(action.payload || {})}`;
  } catch {
    return `${action.type}::${action.label || ''}`;
  }
};

const ObservationActions = ({ observation, onAction, darkMode = true, compact = false }) => {
  const [doneIndex, setDoneIndex] = useState(null);
  const [pendingIndex, setPendingIndex] = useState(null);
  // Fingerprints of actions the user has already successfully executed.
  // We hide those buttons going forward — the analyzer SHOULD also stop
  // emitting them after its cache refreshes, but this is the local safety
  // net that ensures the button doesn't flicker back to "Pin to..." even
  // if the upstream refresh is slow / mis-keyed / missed entirely.
  const [executedFingerprints, setExecutedFingerprints] = useState(() => new Set());

  const allActions = Array.isArray(observation?.actions) ? observation.actions : null;
  if (!allActions || allActions.length === 0) return null;
  if (typeof onAction !== 'function') return null;

  // Filter out actions the user already executed — they're done and gone.
  // Use the fingerprint set rather than indices since the analyzer might
  // re-emit a different subset on the next render.
  const actions = allActions.filter(a => !executedFingerprints.has(actionFingerprint(a)));
  if (actions.length === 0) return null;

  const handleClick = async (action, idx) => {
    if (pendingIndex !== null) return;
    if (action.confirmText) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(action.confirmText);
      if (!ok) return;
    }
    setPendingIndex(idx);
    try {
      await onAction(action, observation);
      setDoneIndex(idx);
      // After a brief "Done ✓" flash, mark the action as executed so the
      // next render filters it out entirely. The doneIndex stays in sync
      // for the flash, then we transition to permanent hidden state.
      setTimeout(() => {
        setExecutedFingerprints(prev => {
          const next = new Set(prev);
          next.add(actionFingerprint(action));
          return next;
        });
        // doneIndex no longer matters once the action is filtered out,
        // but reset it for cleanliness.
        setDoneIndex(prev => (prev === idx ? null : prev));
      }, 1200);
    } catch (e) {
      // Surface failures via console — UI just resets its pending state.
      // eslint-disable-next-line no-console
      console.warn('[ObservationActions] dispatch failed:', e?.message || e);
    } finally {
      setPendingIndex(null);
    }
  };

  const padding = compact ? 'px-2 py-0.5' : 'px-2.5 py-1';
  const text = compact ? 'text-[11px]' : 'text-xs';

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {actions.map((action, idx) => {
        const isDone = doneIndex === idx;
        const isPending = pendingIndex === idx;
        const label = isDone ? (action.doneLabel || 'Done ✓') : action.label;
        return (
          <button
            key={`${action.type}:${idx}`}
            type="button"
            onClick={() => handleClick(action, idx)}
            disabled={isPending || isDone}
            title={action.confirmText || action.label}
            className={`
              inline-flex items-center gap-1 ${padding} ${text} font-medium rounded-md border
              transition-colors
              ${isDone
                ? (darkMode
                    ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-200 cursor-default'
                    : 'bg-emerald-100 border-emerald-300 text-emerald-800 cursor-default')
                : isPending
                  ? (darkMode
                      ? 'bg-white/5 border-white/10 text-gray-500 cursor-wait'
                      : 'bg-gray-100 border-gray-300 text-gray-400 cursor-wait')
                  : (darkMode
                      ? 'bg-white/[0.04] border-white/15 text-gray-200 hover:bg-white/[0.08] hover:border-white/25'
                      : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50 hover:border-gray-400')
              }
            `}
          >
            {isDone && <Check size={11} />}
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ObservationActions;
